import type { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { EventNotification } from '@indutrust/notification-model';
import { resolveHttpError } from '@indutrust/shared-util';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { EventNotificationApiClient } from '../event-notification-api-client.service';

type NotificationState = {
  notifications: EventNotification[];
  isLoading: boolean;
  error: string | null;
};

const initialState: NotificationState = {
  notifications: [],
  isLoading: false,
  error: null,
};

/**
 * Signal store managing event notifications.
 *
 * Provides a list of notifications for the authenticated user, with support
 * for loading all notifications, marking individual notifications as read,
 * and marking all as read.
 *
 * @example
 * const store = inject(NotificationStore);
 * store.loadAll();
 * const count = store.unreadCount();
 */
export const NotificationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    unreadCount: computed(
      () => store.notifications().filter((n) => !n.read).length
    ),
    hasUnread: computed(() => store.notifications().some((n) => !n.read)),
  })),
  withMethods((store, apiClient = inject(EventNotificationApiClient)) => ({
    loadAll: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, {
            isLoading: true,
            error: null,
          });
          return apiClient.getAll().pipe(
            tapResponse({
              next: (notifications) =>
                patchState(store, {
                  notifications,
                  isLoading: false,
                }),
              error: (error: HttpErrorResponse) =>
                patchState(store, {
                  isLoading: false,
                  error: resolveHttpError(error).code,
                }),
            })
          );
        })
      )
    ),

    /** Prepends an optimistic in-app notification (e.g. after client-side attach). */
    prependLocal(notification: EventNotification): void {
      const withoutDuplicate = store
        .notifications()
        .filter(
          (entry) =>
            !(
              entry.event_type === notification.event_type &&
              entry.target_id === notification.target_id &&
              notification.event_type === 'project_factory_added'
            )
        );
      patchState(store, {
        notifications: [notification, ...withoutDuplicate],
      });
    },

    markAsRead(id: number): void {
      patchState(store, {
        notifications: store
          .notifications()
          .map((n) => (n.id === id ? { ...n, read: true } : n)),
      });
      if (id >= 0) {
        apiClient.patch(id, { read: true }).subscribe();
      }
    },

    markAllAsRead(): void {
      patchState(store, {
        notifications: store.notifications().map((n) => ({ ...n, read: true })),
      });
    },
  }))
);
